/**
 * The board pick for a weapon that **attacks several targets at a time** (the Sharanga Missiles'
 * "two targets at a time", the Tiger-Hunter Combat Sheathe's, the Missile Pods' "1-3 characters at
 * a time"): its overlay entry's `attacksTargets` cap. While such a Skirmish or Barrage shot is
 * armed, a board click toggles a unit in or out of the aim's `picks`; the shot fires once the picks
 * reach the cap, or on the targeting banner's CONFIRM with fewer. It is ONE `request_attack` (or one
 * `request_barrage` shot): the first pick is its `target`, the rest its `alsoTargets`, so each takes
 * its own attack roll and all share the damage roll. The engine gates the extra targets (range, line of sight, the cap), so this only collects.
 */
import { weaponInstanceKey, type EncounterState } from "@lancer-vtt/domain";
import { attacksTargets, type LoadedOverlay } from "@lancer-vtt/rules";
import type { RequestAttack } from "@lancer-vtt/shared";
import type { ActionDraft } from "../../../lib/encounter/combatInspector";
import type { TargetingBanner } from "../../../lib/encounter/TargetingMode";
import { directAttackIntent } from "./attackIntentForAim";

type SkirmishAim = Extract<ActionDraft, { mode: "skirmish" }>;
/** The aims that can pick several targets: a Skirmish or a Barrage shot. */
export type PickingAim = Extract<ActionDraft, { mode: "skirmish" | "barrage" }>;

/** Whether `aim` is a Skirmish or Barrage shot, the two that can carry `picks`. */
export function isPickingAim(aim: ActionDraft | null): aim is PickingAim {
  return aim?.mode === "skirmish" || aim?.mode === "barrage";
}

/**
 * How many targets `aim`'s weapon attacks at a time, or `undefined` for a one-target weapon, an
 * Overwatch (a reaction attacks the mover alone) or a fixed-target take.
 */
export function multiTargetCap(args: { state: EncounterState; aim: ActionDraft | null; overlay: LoadedOverlay | undefined }): number | undefined {
  const { state, aim, overlay } = args;
  if (!isPickingAim(aim) || !aim.weaponId) return undefined;
  if (aim.mode === "skirmish" && (aim.reaction || aim.fixedTargetId !== undefined)) return undefined;
  const weapon = state.combatants[aim.attackerId]?.base.weapons?.find((w) => weaponInstanceKey(w) === aim.weaponId);
  const cap = weapon ? attacksTargets(overlay, weapon.id) : undefined;
  return cap !== undefined && cap > 1 ? cap : undefined;
}

/** `picks` with `id` toggled in or out, in click order. */
export function togglePick(picks: readonly string[], id: string): string[] {
  return picks.includes(id) ? picks.filter((p) => p !== id) : [...picks, id];
}

/** The one attack at `picks`: the first is the target, the rest ride `alsoTargets`. */
export function multiTargetIntent(state: EncounterState, aim: SkirmishAim, picks: readonly string[]): RequestAttack {
  const intent = directAttackIntent(state, aim, picks[0]!);
  return picks.length > 1 ? { ...intent, alsoTargets: picks.slice(1) } : intent;
}

/** The targeting banner while such a weapon is armed: its cap, then what is picked so far. */
export function multiTargetBanner(base: TargetingBanner, args: { cap: number; names: readonly string[] }): TargetingBanner {
  const picked = args.names.length > 0 ? args.names.join(", ").toUpperCase() : "NONE";
  return { ...base, sub: `CLICK UP TO ${args.cap} TARGETS, THEN CONFIRM · PICKED: ${picked}` };
}
